import { create } from 'zustand';
import { ApiService } from '@/shared/services/api.service';
import { WelcomeSettingsDto } from 'nexgen-types';

// Track the last orgSlug that was successfully loaded
let lastLoadedSlug: string | null = null;

interface WelcomeStore {
  welcomeSettings: WelcomeSettingsDto | null;
  isLoading: boolean;
  error: string | null;
  errorReason?: 'welcome_not_found' | 'login_error' | 'other' | null;
  loadWelcomeSettings: (orgslug: string) => Promise<void>;
  clearWelcomeData: () => void;
  setErrorReason: (reason: 'welcome_not_found' | 'login_error' | 'other' | null) => void;
}

export const useWelcomeStore = create<WelcomeStore>((set) => ({
  welcomeSettings: null,
  isLoading: false,
  error: null,
  errorReason: null,

  loadWelcomeSettings: async (orgslug) => {
    if (lastLoadedSlug === orgslug) return;
    set({ isLoading: true, error: null });

    try {
      const fetchedData = await ApiService.request<WelcomeSettingsDto>({
        endpoint: `welcome/${orgslug}`,
      });
      localStorage.setItem('org-slug', orgslug);
      localStorage.setItem('orgPid', fetchedData.pid);

      lastLoadedSlug = orgslug;
      set({ welcomeSettings: fetchedData, isLoading: false });
    } catch (error: any) {
      console.error(`Failed to load welcome settings for ${orgslug}:`, error.message);
      set({
        error: `Failed to load organization settings.`,
        isLoading: false,
        errorReason:
          error.status === 404
            ? 'welcome_not_found'
            : error.status === 401
              ? 'login_error'
              : 'other',
      });
    }
  },

  setErrorReason: (reason) => set({ errorReason: reason }),

  clearWelcomeData: () => {
    lastLoadedSlug = null;
    set({ welcomeSettings: null, error: null, errorReason: null });
  },
}));
